import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Briefcase, Trash2, XCircle } from 'lucide-react';
import toast from 'react-hot-toast';
import {
  jobSearchStatusLabel,
  jobService,
  type JobSearch,
  type JobSearchStatus,
} from '../services/jobService';
import { fieldClass, labelClass, formatDateTime } from '../utils/form';

const emptyForm = () => ({
  sourceSlug: 'jobinja',
  requestedLimit: 40,
  useProfile: true,
  enableMatch: true,
  useAi: false,
  minMatch: 70,
});

function statusBadge(status: JobSearchStatus) {
  const styles: Record<JobSearchStatus, string> = {
    pending: 'bg-slate-100 text-slate-700',
    running: 'bg-amber-100 text-amber-800',
    completed: 'bg-emerald-100 text-emerald-800',
    failed: 'bg-rose-100 text-rose-800',
    cancelled: 'bg-slate-100 text-slate-500',
  };
  return (
    <span className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-bold ${styles[status]}`}>
      {jobSearchStatusLabel[status]}
    </span>
  );
}

export function JobSearchPage() {
  const [form, setForm] = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);
  const [searches, setSearches] = useState<JobSearch[]>([]);
  const [loading, setLoading] = useState(true);
  const [sources, setSources] = useState<Array<Record<string, unknown>>>([]);

  const hasActive = useMemo(
    () => searches.some((j) => j.status === 'pending' || j.status === 'running'),
    [searches],
  );

  const load = useCallback(async () => {
    try {
      const [res, src] = await Promise.all([
        jobService.listSearches({ page: 1, limit: 30 }),
        jobService.listSources(),
      ]);
      setSearches(res?.data ?? []);
      setSources(src);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'خطا در دریافت جستجوها');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (!hasActive) return;
    const t = setInterval(() => {
      void load();
    }, 3000);
    return () => clearInterval(t);
  }, [hasActive, load]);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await jobService.createSearch({
        sourceSlug: form.sourceSlug,
        requestedLimit: form.requestedLimit,
        useProfile: form.useProfile,
        enableMatch: form.enableMatch,
        useAi: form.useAi,
        minMatch: form.minMatch,
      });
      toast.success('جستجوی شغل ساخته شد');
      setForm(emptyForm());
      await load();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'خطا در ایجاد جستجو');
    } finally {
      setSubmitting(false);
    }
  };

  const onCancel = async (id: string) => {
    try {
      await jobService.cancelSearch(id);
      toast.success('لغو شد');
      await load();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'خطا در لغو');
    }
  };

  const onDelete = async (id: string) => {
    if (!window.confirm(`جاب جستجو #${id} حذف شود؟ (آگهی‌های ذخیره‌شده پاک نمی‌شوند)`)) return;
    try {
      await jobService.deleteSearch(id);
      toast.success('جاب حذف شد');
      await load();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'خطا در حذف');
    }
  };

  const enabledSources = sources.filter((s) => s.isEnabled);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold text-slate-900">هوشمندی شغلی</h1>
          <p className="mt-1 text-sm text-slate-500">
            کراول واقعی Jobinja + JobVision (sitemap)، تطبیق قطعی با پروفایل، AI اختیاری
          </p>
        </div>
        <Link to="/jobs" className="text-sm font-semibold text-rose-600 hover:underline">
          مشاهده آگهی‌ها و امتیازها →
        </Link>
      </div>

      <form onSubmit={onSubmit} className="grid gap-4 rounded-2xl border border-slate-200 bg-white p-4 sm:grid-cols-2 lg:grid-cols-3">
        <div>
          <label className={labelClass}>منبع</label>
          <select
            className={fieldClass}
            value={form.sourceSlug}
            onChange={(e) => setForm((f) => ({ ...f, sourceSlug: e.target.value }))}
          >
            {(enabledSources.length ? enabledSources : [{ slug: 'jobinja', name: 'Jobinja' }]).map((s) => (
              <option key={String(s.slug)} value={String(s.slug)}>
                {String(s.name)}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className={labelClass}>سقف آگهی</label>
          <input
            type="number"
            min={1}
            max={200}
            className={fieldClass}
            value={form.requestedLimit}
            onChange={(e) => setForm((f) => ({ ...f, requestedLimit: Number(e.target.value) || 40 }))}
          />
        </div>
        <div>
          <label className={labelClass}>حداقل امتیاز</label>
          <input
            type="number"
            min={0}
            max={100}
            className={fieldClass}
            value={form.minMatch}
            onChange={(e) => setForm((f) => ({ ...f, minMatch: Number(e.target.value) || 0 }))}
          />
        </div>
        <label className="flex items-center gap-2 text-sm text-slate-700">
          <input
            type="checkbox"
            checked={form.useProfile}
            onChange={(e) => setForm((f) => ({ ...f, useProfile: e.target.checked }))}
          />
          استفاده از پروفایل (کلیدواژه/شهر/مهارت)
        </label>
        <label className="flex items-center gap-2 text-sm text-slate-700">
          <input
            type="checkbox"
            checked={form.enableMatch}
            onChange={(e) => setForm((f) => ({ ...f, enableMatch: e.target.checked }))}
          />
          محاسبه تطبیق
        </label>
        <label className="flex items-center gap-2 text-sm text-slate-700">
          <input
            type="checkbox"
            checked={form.useAi}
            onChange={(e) => setForm((f) => ({ ...f, useAi: e.target.checked }))}
          />
          AI (Ollama) — فعلاً اختیاری/آماده‌سازی
        </label>
        <div className="sm:col-span-2 lg:col-span-3">
          <button
            type="submit"
            disabled={submitting}
            className="inline-flex items-center gap-2 rounded-xl bg-rose-600 px-4 py-2.5 text-sm font-bold text-white hover:bg-rose-700 disabled:opacity-60"
          >
            <Briefcase className="h-4 w-4" />
            {submitting ? 'در حال ارسال…' : 'شروع جستجو'}
          </button>
        </div>
      </form>

      <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white">
        <table className="min-w-full text-sm">
          <thead className="bg-slate-50 text-slate-600">
            <tr>
              <th className="px-3 py-2 text-right font-semibold">#</th>
              <th className="px-3 py-2 text-right font-semibold">منبع</th>
              <th className="px-3 py-2 text-right font-semibold">وضعیت</th>
              <th className="px-3 py-2 text-right font-semibold">پیشرفت</th>
              <th className="px-3 py-2 text-right font-semibold">پرچم‌ها</th>
              <th className="px-3 py-2 text-right font-semibold">زمان</th>
              <th className="px-3 py-2 text-right font-semibold" />
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={7} className="px-3 py-8 text-center text-slate-400">
                  در حال بارگذاری…
                </td>
              </tr>
            ) : searches.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-3 py-8 text-center text-slate-400">
                  هنوز جستجویی نیست
                </td>
              </tr>
            ) : (
              searches.map((j) => (
                <tr key={j.id} className="border-t border-slate-100">
                  <td className="px-3 py-2 font-mono text-xs">{j.id}</td>
                  <td className="px-3 py-2">{j.sourceSlug}</td>
                  <td className="px-3 py-2">{statusBadge(j.status)}</td>
                  <td className="px-3 py-2 text-xs text-slate-600">
                    صفحه {j.pagesCrawled} · پیدا {j.jobsFound} · ذخیره {j.jobsSaved} · match {j.matchesWritten}
                    {j.errorMessage ? <div className="text-rose-600">{j.errorMessage}</div> : null}
                  </td>
                  <td className="px-3 py-2 text-xs">
                    {j.useProfile ? 'پروفایل ' : ''}
                    {j.enableMatch ? 'تطبیق ' : ''}
                    {j.useAi ? 'AI' : ''}
                  </td>
                  <td className="px-3 py-2 text-xs text-slate-500">{formatDateTime(j.createdAt)}</td>
                  <td className="px-3 py-2">
                    <div className="flex items-center justify-end gap-3">
                      {(j.status === 'pending' || j.status === 'running') && (
                        <button
                          type="button"
                          onClick={() => void onCancel(j.id)}
                          className="inline-flex items-center gap-1 text-xs font-semibold text-amber-700"
                        >
                          <XCircle className="h-3.5 w-3.5" />
                          لغو
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => void onDelete(j.id)}
                        className="inline-flex items-center gap-1 text-xs font-semibold text-rose-600"
                        title="حذف جاب"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                        حذف
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
